/**
 * Shared reader for the `payrailsRiskMigrationConfig` site preference.
 *
 * The preference is a JSON array of rules. Each rule may carry a
 * `payrailsRiskSDK` boolean: when `true` on any rule, the Payrails Web Fraud
 * SDK must be loaded on the storefront (replacing the native Signifyd
 * device-fingerprint script and the Riskified beacon). This is a site-wide
 * client-side toggle, because device fingerprinting starts at app load,
 * before a payment method is chosen on the basket.
 *
 * Used by `usePayrailsFraudSDK`, `useSignifydFingerprint` and
 * `useLoadRiskifiedScript` so the decision is parsed from the preference in
 * one place.
 */

interface PayrailsRiskMigrationRule {
	paymentMethod?: string;
	adviseCheckDisable?: boolean;
	riskCheckDisable?: boolean;
	riskCancelDisable?: boolean;
	payrailsRiskSDK?: boolean;
}

/**
 * Normalizes the parsed preference value into a rules array.
 * Accepts the canonical shape `{ "rules": [ ... ] }` and falls back to a
 * bare array for backward compatibility. Returns `null` for any other shape.
 */
const normalizeMigrationRules = (parsed: unknown): PayrailsRiskMigrationRule[] | null => {
	if (Array.isArray(parsed)) {
		return parsed;
	}

	if (parsed && typeof parsed === 'object' && 'rules' in parsed && Array.isArray((parsed as Record<string, unknown>).rules)) {
		return (parsed as Record<string, unknown>).rules as PayrailsRiskMigrationRule[];
	}

	return null;
};

/**
 * Parses the raw `payrailsRiskMigrationConfig` preference value and returns
 * `true` when at least one rule declares `payrailsRiskSDK === true`.
 * Never throws; malformed JSON is ignored (returns `false`).
 * @param prefValue - raw preference value (JSON string or already-parsed object/array)
 * @returns true when the Payrails Fraud SDK should be active on the site
 */
export const isPayrailsRiskSDKActive = (prefValue: unknown): boolean => {
	if (!prefValue) {
		return false;
	}

	let parsed: unknown;

	if (typeof prefValue === 'string') {
		try {
			parsed = JSON.parse(prefValue);
		} catch {
			return false;
		}
	} else {
		parsed = prefValue;
	}

	const rules = normalizeMigrationRules(parsed);

	if (!rules || rules.length === 0) {
		return false;
	}

	return rules.some((rule) => Boolean(rule && rule.payrailsRiskSDK === true));
};
